import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface JsonLdProps {
  /** Optional page title override; defaults to document.title at mount. */
  name?: string;
  /** Optional page description; defaults to <meta name="description"> content. */
  description?: string;
  /** Additional Schema.org nodes to inject alongside the WebPage node. */
  extra?: Record<string, unknown>[];
}

const SITE = "https://www.freecrm.com";

/**
 * Injects a per-page Schema.org JSON-LD <script> with WebPage + publisher
 * pointing to the FreeCRM Organization defined in index.html.
 */
const JsonLd = ({ name, description, extra = [] }: JsonLdProps) => {
  const location = useLocation();

  useEffect(() => {
    const pageName = name ?? (typeof document !== "undefined" ? document.title : "FreeCRM");
    const metaDesc =
      description ??
      (typeof document !== "undefined"
        ? document.querySelector('meta[name="description"]')?.getAttribute("content") ?? ""
        : "");
    const url = `${SITE}${location.pathname}`;

    const graph: Record<string, unknown>[] = [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: pageName,
        description: metaDesc,
        isPartOf: { "@id": `${SITE}/#website` },
        publisher: { "@id": `${SITE}/#organization` },
        inLanguage: "en",
      },
      ...extra,
    ];

    const payload = {
      "@context": "https://schema.org",
      "@graph": graph,
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-jsonld", "page");
    script.text = JSON.stringify(payload);

    // Remove any previously injected page-level JSON-LD before adding the new one.
    document
      .querySelectorAll('script[data-jsonld="page"]')
      .forEach((el) => el.parentNode?.removeChild(el));

    document.head.appendChild(script);

    return () => {
      script.parentNode?.removeChild(script);
    };
  }, [name, description, location.pathname, JSON.stringify(extra)]);

  return null;
};

export default JsonLd;
